import { h } from '@atomify/jsx';
import { CheckboxElement } from '@atoms/form-elements/checkbox/checkbox';
import { Image } from '@atoms/image';
import { AreaSubscriptionListCardInterface } from '@features/area-subscription/types';
import { COLOR_CLASSES } from '@utilities/api-formatters';
import { translateItem } from "@utilities/hooks";

export const TOGGLE_INPUT_HOOK = 'area-subscription-list-card-toggle-input';
const CHECKBOX_HOVER_CLASS = 'checkbox__input--force-hover';

export const AreaSubscriptionListCard = (data: AreaSubscriptionListCardInterface) => {
    const {
        id,
        classes = '',
        image,
        title,
        status,
        children,
        selectable,
        checkboxLabel,
        onItemClick,
    } = data;

    const card = (
        <button
            class={`c-area-subscription-list-card ${
                selectable ? 'area-subscription-list-card--selectable' : ''
            } ${classes}`.trim()}
            onClick={handleClick}>
            <div class="area-subscription-list-card__inner">
                {image && (
                    <Image
                        image={image}
                        preload={{ width: 16, height: 12 }}
                        settings={{
                            '320w': {
                                width: 320,
                                height: 240,
                            },
                            '480w': {
                                width: 480,
                                height: 320,
                            },
                            '640w': {
                                width: 640,
                                height: 480,
                            },
                            '800w': {
                                width: 800,
                                height: 600,
                            },
                        }}
                        classes="area-subscription-list-card__image"
                        alt={`${title} ${id}`}
                        objectFit={true}
                    />
                )}
                <div class="area-subscription-list-card__details">
                    <div>
                        {status ? (
                            <span class={`u-body-normal ${COLOR_CLASSES[status.color]}`}>
                                {status.label}
                            </span>
                        ) : null}
                        <div class="u-margin-top-2">
                            <p class="u-body-normal-bold">{title}</p>
                            {children ? <div class="s-rich-text">{children}</div> : null}
                        </div>
                    </div>

                    {selectable && checkboxLabel ? (
                        <CheckboxElement
                            label={checkboxLabel}
                            hook={TOGGLE_INPUT_HOOK}
                            value={id}
                            id={`area-subscription-list-card-checkbox-${id}`}
                            name="area-subscription-list-card-checkbox"
                            classes="area-subscription__checkbox area-subscription-list-card__checkbox"
                            hideError
                        />
                    ) : null}
                </div>
            </div>
        </button>
    ) as HTMLButtonElement;

    async function handleClick() {
        const toggle = card.querySelector<HTMLInputElement>(`[js-hook-${TOGGLE_INPUT_HOOK}]`)!;

        toggle.checked = !toggle.checked;
        onItemClick && onItemClick(id, toggle.checked);

        const parentCheckboxContainer = document.querySelector<HTMLElement>('[js-hook-area-subscription-subscribe-area-only-toggle]');
        const parentCheckboxInput = parentCheckboxContainer?.querySelector<HTMLInputElement>('[js-hook-area-subscription-subscribe-area-only-toggle-input]');
        const parentCheckboxLabel = parentCheckboxContainer?.querySelector<HTMLElement>('[js-hook-area-subscription-subscribe-area-only-toggle-label]');
        const disabledLabel = await translateItem('AreaSubscription.Modal.Step1.AreaOnlyToggleLabelDisabled');
        if (parentCheckboxInput && parentCheckboxLabel) {
            if (!parentCheckboxLabel.getAttribute('regular-label')) {
                parentCheckboxLabel.setAttribute('regular-label', parentCheckboxLabel.innerText);
            }
            if (toggle.checked) {
                parentCheckboxInput.disabled = true;
                parentCheckboxContainer?.classList.add('disabled');
                parentCheckboxLabel.innerText = disabledLabel;
            } else {
                parentCheckboxInput.disabled = false;
                parentCheckboxContainer?.classList.remove('disabled');
                parentCheckboxLabel.innerText = parentCheckboxLabel.getAttribute('regular-label') || '';
            }
        }
    }

    const setToggleHover = (hover: boolean) => {
        const toggle = card.querySelector<HTMLInputElement>(`[js-hook-${TOGGLE_INPUT_HOOK}]`)!;

        toggle.classList[hover ? 'add' : 'remove'](CHECKBOX_HOVER_CLASS);
    };

    card.addEventListener('mouseenter', () => setToggleHover(true));
    card.addEventListener('mouseleave', () => setToggleHover(false));

    return card;
};
